export interface TelegramWidgetAuthUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

let telegramUiConfigured = false;

function asTrimmedString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function postTelegramProxyEvent(
  eventType: string,
  payload: Record<string, unknown> = {},
): void {
  if (typeof window === "undefined") {
    return;
  }

  const proxy = window.TelegramWebviewProxy;
  if (!proxy || typeof proxy.postEvent !== "function") {
    return;
  }

  try {
    proxy.postEvent(eventType, payload);
    return;
  } catch {
    // fallthrough
  }

  try {
    proxy.postEvent(eventType, JSON.stringify(payload));
  } catch {
    // ignore bridge errors
  }
}

export function isTelegramMiniAppEnvironment(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  if (window.TelegramWebviewProxy) {
    return true;
  }

  const initData = asTrimmedString(window.Telegram?.WebApp?.initData);
  return initData.length > 0;
}

export function getTelegramWebAppInitData(): string {
  if (typeof window === "undefined") {
    return "";
  }

  return asTrimmedString(window.Telegram?.WebApp?.initData);
}

export function setupTelegramMiniAppUi(): void {
  if (telegramUiConfigured || !isTelegramMiniAppEnvironment()) {
    return;
  }

  telegramUiConfigured = true;

  try {
    window.Telegram?.WebApp?.ready();
  } catch {
    // ignore
  }

  try {
    window.Telegram?.WebApp?.setHeaderColor?.("#ffffff");
  } catch {
    // ignore
  }

  try {
    window.Telegram?.WebApp?.BackButton?.hide();
  } catch {
    // ignore
  }

  try {
    window.Telegram?.WebApp?.SettingsButton?.hide();
  } catch {
    // ignore
  }

  try {
    window.Telegram?.WebApp?.expand();
  } catch {
    // ignore
  }

  postTelegramProxyEvent("web_app_set_header_color", {
    color_key: "bg_color",
    color: "#ffffff",
  });
  postTelegramProxyEvent("web_app_setup_settings_button", {
    is_visible: false,
  });
  postTelegramProxyEvent("web_app_setup_back_button", {
    is_visible: false,
  });
  postTelegramProxyEvent("web_app_expand");
}

export function normalizeTelegramWidgetAuthUser(
  value: unknown,
): TelegramWidgetAuthUser | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const id = Number(record.id);
  const firstName = asTrimmedString(record.first_name);
  const authDate = Number(record.auth_date);
  const hash = asTrimmedString(record.hash);

  if (!Number.isFinite(id) || id <= 0) {
    return null;
  }

  if (!firstName) {
    return null;
  }

  if (!Number.isFinite(authDate) || authDate <= 0) {
    return null;
  }

  if (!hash) {
    return null;
  }

  const lastName = asTrimmedString(record.last_name);
  const username = asTrimmedString(record.username);
  const photoUrl = asTrimmedString(record.photo_url);

  return {
    id: Math.trunc(id),
    first_name: firstName,
    last_name: lastName || undefined,
    username: username || undefined,
    photo_url: photoUrl || undefined,
    auth_date: Math.trunc(authDate),
    hash,
  };
}
